/**
 * Clean this up along with aifc_create_enabled
 */
import React, { useState, useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type {
	ObjectSidebarEditorAIAction,
	ObjectSidebarEditorAIActionSubItem,
} from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

const popupStyles = cssMap({
	container: {
		paddingBlock: token('space.0'),
	},
});

type PopupContentProps = {
	title: MessageDescriptor;
	subActionItems: ObjectSidebarEditorAIActionSubItem[];
	onSubItemClick: () => void;
};

const PopupContent = ({ title, subActionItems, onSubItemClick }: PopupContentProps) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={popupStyles.container}>
			<MenuGroup>
				<Section title={formatMessage(title)}>
					{subActionItems.map((actionItem) => {
						const Icon = actionItem.icon;
						const onClick = () => {
							onSubItemClick();
							actionItem.onClick();
						};
						return (
							<ButtonItem
								key={actionItem.title.id}
								onClick={onClick}
								iconBefore={<Icon size="small" label={formatMessage(actionItem.title)} />}
							>
								{formatMessage(actionItem.title)}
							</ButtonItem>
						);
					})}
				</Section>
			</MenuGroup>
		</Box>
	);
};

type ObjectSidebarEditorAIActionWithSubItems = Extract<
	ObjectSidebarEditorAIAction,
	{ subItems: ObjectSidebarEditorAIActionSubItem[] }
>;
const EditorAIActionButtonWithSubItems = ({
	action,
	isDisabled,
}: {
	action: ObjectSidebarEditorAIActionWithSubItems;
	isDisabled?: boolean;
}) => {
	const { formatMessage } = useIntl();

	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
	const onClick = useCallback(() => {
		setIsPopupOpen((prev) => !prev);
	}, [setIsPopupOpen]);
	const onClose = useCallback(() => {
		setIsPopupOpen(false);
	}, [setIsPopupOpen]);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isPopupOpen}
			onClose={onClose}
			placement="left"
			content={() => (
				<PopupContent
					title={action.title}
					subActionItems={action.subItems}
					onSubItemClick={onClose}
				/>
			)}
			trigger={(triggerProps) => (
				<Tooltip position="left" content={formatMessage(action.title)} hideTooltipOnClick>
					<IconButton
						{...triggerProps}
						icon={action.icon}
						appearance="subtle"
						label={formatMessage(action.title)}
						testId={action.title.id}
						onClick={onClick}
						tooltip={{
							position: 'left',
						}}
						isDisabled={isDisabled}
					/>
				</Tooltip>
			)}
		/>
	);
};

type EditorAIActionsProps = {
	editorAIActions: ObjectSidebarEditorAIAction[];
	isDisabled?: boolean;
};
export const EditorAIActions = ({ editorAIActions, isDisabled }: EditorAIActionsProps) => {
	const { formatMessage } = useIntl();

	const editorActionItems = editorAIActions.map((action) => {
		if ('subItems' in action) {
			return (
				<EditorAIActionButtonWithSubItems
					key={action.title.id}
					action={action}
					isDisabled={isDisabled}
				/>
			);
		}
		return (
			<Tooltip
				key={action.title.id}
				position="left"
				content={formatMessage(action.title)}
				hideTooltipOnClick
			>
				<IconButton
					icon={action.icon}
					appearance="subtle"
					label={formatMessage(action.title)}
					onClick={action.onClick}
					isDisabled={isDisabled}
				/>
			</Tooltip>
		);
	});

	return <>{editorActionItems}</>;
};
